import { CheckUserDNIResponse, LoginParamsType, Token } from '@/interfaces/login';
import { jwtDecode } from 'jwt-decode';
import type { JwtPayload } from 'jwt-decode';
import { AxiosResponse, AxiosPromise } from 'axios';
import request, { AxiosRequestCustomConfig } from '@/umi-request';

let PERMISSIONS: string[] = [];

export function getToken(): string | null {
  return localStorage.getItem('token');
}

export function getRefresh(): string | null {
  return localStorage.getItem('refresh');
}

export function setToken(token: Token): void {
  localStorage.setItem('token', token.access);
  localStorage.setItem('refresh', token.refresh);
}

export function hasPermission(permission: string): boolean {
  return PERMISSIONS.includes(permission);
}

export function hasAnyPermissions(permissions: string[]): boolean {
  return permissions.some((p) => hasPermission(p));
}

export function hasPermissions(permissions: string[]): boolean {
  return permissions.every((p) => hasPermission(p));
}

export function setPermissions(permissions: string[]): void {
  PERMISSIONS = permissions;
}

export function isValid(): boolean {
  const token = getToken();
  if (token) {
    const { exp } = jwtDecode<JwtPayload>(token);
    return exp ? exp > Date.now() / 1000 : false;
  }
  return false;
}

export function isRefreshValid(): boolean {
  const token = getRefresh();
  if (token) {
    const { exp } = jwtDecode<JwtPayload>(token);
    return exp ? exp > Date.now() / 1000 : false;
  }
  return false;
}

export async function closeSession(params?: any, cancelToken?: any) {
  return request({
    url: `users/me/logout-sso/`,
    method: 'PUT',
    data: { params, cancelToken }
  }) as AxiosPromise;
}

export function outLogin(): void {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh');
  PERMISSIONS = [];
}

export async function login(data: LoginParamsType): Promise<Token> {
  return request({
    method: 'POST',
    url: 'token/',
    data,
    skipAuth: true,
  } as AxiosRequestCustomConfig).then((response: AxiosResponse<Token>) => {
    setToken(response.data);
    recordLogin();
    return response.data;
  });
}

export async function refresh(): Promise<Token> {
  return request({
    method: 'POST',
    url: 'token/refresh/',
    data: { refresh: getRefresh() },
    skipAuth: true,
  } as AxiosRequestCustomConfig).then((response: AxiosResponse<Token>) => {
    setToken(response.data);
    return response.data;
  });
}

export async function recordLogin() {
  return request({
    method: 'POST',
    url: `users/me/record-login/`,
  }) as AxiosPromise;
}

export async function requestFirstLogin(data: { email: string; dni: string; dpas_number: string }) {
  return request({
    url: `users/first-access/`,
    method: 'POST',
    data
  }) as AxiosPromise;
}

export async function requestNewPassword(data: { email: string }) {
  return request({
    url: `users/request-change-password/`,
    method: 'PUT',
    data
  }) as AxiosPromise;
}

export async function changePassword(data: { email: string; token: string; password: string }) {
  return request({
    url: `users/do-change-password/`,
    method: 'PUT',
    data
  }) as AxiosPromise;
}

export async function check_dni(data: any) {
  return request({
    url: 'users/user_dni/',
    method: 'POST',
    data,
    skipAuth: true,
  } as AxiosRequestCustomConfig) as AxiosPromise<AxiosResponse<CheckUserDNIResponse>>;
}
